import React, { useState, useEffect } from 'react';
import {
  DEFAULT_AVATAR_PATH,
  DEFAULT_AVATAR_SVG_DATA_URI,
  DEFAULT_AVATAR_SVG_RAW,
} from '../utils/defaultAvatarSvg';
import {
  DEFAULT_FEMALE_AVATAR_PATH,
  DEFAULT_FEMALE_AVATAR_SVG_DATA_URI,
  DEFAULT_FEMALE_AVATAR_SVG_RAW,
} from '../utils/femaleAvatarSvg';
import { useSite } from '../context/SiteContext';
import { SiteTarget } from '../types';

export {
  DEFAULT_AVATAR_PATH,
  DEFAULT_AVATAR_SVG_DATA_URI,
  DEFAULT_AVATAR_SVG_RAW,
  DEFAULT_FEMALE_AVATAR_PATH,
  DEFAULT_FEMALE_AVATAR_SVG_DATA_URI,
  DEFAULT_FEMALE_AVATAR_SVG_RAW,
};

const STORAGE_KEY_MALE = 'bursa_uzman_samet_photo';
const STORAGE_KEY_FEMALE = 'bursa_uzman_kadin_photo';
const STORAGE_KEY_APOLLON = 'bursa_uzman_apollon_photo';

let globalSpecialistPhotoMale = DEFAULT_AVATAR_PATH;
let globalSpecialistPhotoFemale = DEFAULT_FEMALE_AVATAR_PATH;
let hasFetchedMaleFromServer = false;
let hasFetchedFemaleFromServer = false;

export const getStoredSpecialistPhoto = (site: SiteTarget = 'erkek'): string => {
  const isFemale = site === 'kadin';
  const defaultPath = isFemale ? DEFAULT_FEMALE_AVATAR_PATH : DEFAULT_AVATAR_PATH;
  const storageKey = site === 'apollon' ? STORAGE_KEY_APOLLON : isFemale ? STORAGE_KEY_FEMALE : STORAGE_KEY_MALE;

  if (typeof window !== 'undefined') {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) return cached;
      // Fallback: If site is erkek or apollon, check each other's storage to stay 100% in sync
      if (site === 'erkek' || site === 'apollon') {
        const altCached = localStorage.getItem(site === 'erkek' ? STORAGE_KEY_APOLLON : STORAGE_KEY_MALE);
        if (altCached) return altCached;
      }
    } catch {
      // Storage access may be blocked in sandboxed iframes
    }
  }
  return (isFemale ? globalSpecialistPhotoFemale : globalSpecialistPhotoMale) || defaultPath;
};

export const setStoredSpecialistPhoto = (photoDataUrl: string, site: SiteTarget = 'erkek') => {
  const isFemale = site === 'kadin';
  const defaultPath = isFemale ? DEFAULT_FEMALE_AVATAR_PATH : DEFAULT_AVATAR_PATH;
  const defaultSvgDataUri = isFemale ? DEFAULT_FEMALE_AVATAR_SVG_DATA_URI : DEFAULT_AVATAR_SVG_DATA_URI;
  const storageKey = site === 'apollon' ? STORAGE_KEY_APOLLON : isFemale ? STORAGE_KEY_FEMALE : STORAGE_KEY_MALE;
  const defaultSvgFilename = isFemale ? 'uzman-merve.svg' : 'uzman-samet.svg';

  if (typeof window !== 'undefined') {
    try {
      if (
        photoDataUrl &&
        photoDataUrl !== defaultPath &&
        photoDataUrl !== defaultSvgDataUri &&
        !photoDataUrl.includes(defaultSvgFilename)
      ) {
        localStorage.setItem(storageKey, photoDataUrl);
        // Keep Erkek and Apollon in exact sync
        if (site === 'erkek') {
          localStorage.setItem(STORAGE_KEY_APOLLON, photoDataUrl);
        } else if (site === 'apollon') {
          localStorage.setItem(STORAGE_KEY_MALE, photoDataUrl);
        }

        if (isFemale) {
          globalSpecialistPhotoFemale = photoDataUrl;
        } else {
          globalSpecialistPhotoMale = photoDataUrl;
        }
      } else {
        localStorage.removeItem(storageKey);
        if (site === 'erkek') localStorage.removeItem(STORAGE_KEY_APOLLON);
        if (site === 'apollon') localStorage.removeItem(STORAGE_KEY_MALE);

        if (isFemale) {
          globalSpecialistPhotoFemale = defaultPath;
        } else {
          globalSpecialistPhotoMale = defaultPath;
        }
      }
    } catch {
      if (isFemale) {
        globalSpecialistPhotoFemale = photoDataUrl || defaultPath;
      } else {
        globalSpecialistPhotoMale = photoDataUrl || defaultPath;
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent('specialist-photo-changed', {
          detail: {
            photo: photoDataUrl || defaultPath,
            site,
          },
        })
      );
    } catch {}
  }
};

// Sync with server on startup or after upload
export const syncSpecialistPhotoWithServer = async (site: SiteTarget = 'erkek') => {
  const isFemale = site === 'kadin' || site === 'apollon';
  const defaultPath = isFemale ? DEFAULT_FEMALE_AVATAR_PATH : DEFAULT_AVATAR_PATH;
  const defaultSvgFilename = isFemale ? 'uzman-merve.svg' : 'uzman-samet.svg';
  const storageKey = site === 'apollon' ? STORAGE_KEY_APOLLON : isFemale ? STORAGE_KEY_FEMALE : STORAGE_KEY_MALE;

  try {
    const res = await fetch(`/api/specialist-profile?site=${site}&_t=${Date.now()}`);
    if (res.ok) {
      const data = await res.json();
      let clientCached: string | null = null;
      try {
        clientCached = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
      } catch {}

      const isServerDefault =
        !data.hasCustomPhoto ||
        !data.photoUrl ||
        data.photoUrl === defaultPath ||
        data.photoUrl.includes(defaultSvgFilename) ||
        data.photoUrl.startsWith('data:image/svg+xml');

      if (isServerDefault) {
        // Server confirms default SVG avatar state - wipe any local storage to avoid ghost overrides
        if (isFemale) {
          globalSpecialistPhotoFemale = defaultPath;
        } else {
          globalSpecialistPhotoMale = defaultPath;
        }
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem(storageKey);
          } catch {}
          try {
            window.dispatchEvent(
              new CustomEvent('specialist-photo-changed', {
                detail: { photo: defaultPath, site },
              })
            );
          } catch {}
        }
      } else if (data.photoUrl) {
        // Server has a valid custom photo - use and cache it
        if (isFemale) {
          globalSpecialistPhotoFemale = data.photoUrl;
        } else {
          globalSpecialistPhotoMale = data.photoUrl;
        }
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(storageKey, data.photoUrl);
          } catch {}
          try {
            window.dispatchEvent(
              new CustomEvent('specialist-photo-changed', {
                detail: { photo: data.photoUrl, site },
              })
            );
          } catch {}
        }
      } else if (clientCached && clientCached.startsWith('data:image/') && !clientCached.startsWith('data:image/svg+xml')) {
        // Only re-push client cache if it is a genuine local custom upload
        if (isFemale) {
          globalSpecialistPhotoFemale = clientCached;
        } else {
          globalSpecialistPhotoMale = clientCached;
        }
        try {
          window.dispatchEvent(
            new CustomEvent('specialist-photo-changed', {
              detail: { photo: clientCached, site },
            })
          );
        } catch {}

        // Push client cached photo to server in background
        fetch('/api/specialist-profile/sync-client-photo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ photoData: clientCached, site }),
        }).catch(() => {});
      } else {
        if (isFemale) {
          globalSpecialistPhotoFemale = defaultPath;
        } else {
          globalSpecialistPhotoMale = defaultPath;
        }
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem(storageKey);
          } catch {}
          try {
            window.dispatchEvent(
              new CustomEvent('specialist-photo-changed', {
                detail: { photo: defaultPath, site },
              })
            );
          } catch {}
        }
      }
    }
  } catch (e) {
    // offline or static fallback
    try {
      const clientCached = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
      if (clientCached) {
        if (isFemale) {
          globalSpecialistPhotoFemale = clientCached;
        } else {
          globalSpecialistPhotoMale = clientCached;
        }
      }
    } catch {}
  }
};

export type SpecialistAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface SpecialistAvatarProps {
  size?: SpecialistAvatarSize;
  showOnlineStatus?: boolean;
  className?: string;
  bordered?: boolean;
  onClick?: () => void;
  site?: SiteTarget;
}

export const SpecialistAvatar: React.FC<SpecialistAvatarProps> = ({
  size = 'md',
  showOnlineStatus = true,
  className = '',
  bordered = true,
  onClick,
  site,
}) => {
  // Gracefully read site from context if available
  let siteFromContext: SiteTarget = 'erkek';
  try {
    const context = useSite();
    if (context && context.siteKey) {
      siteFromContext = context.siteKey;
    }
  } catch {
    // If rendered outside SiteProvider, fallback to 'erkek'
  }

  const effectiveSite: SiteTarget = site || siteFromContext;
  const isFemale = effectiveSite === 'kadin' || effectiveSite === 'apollon';
  const defaultPath = isFemale ? DEFAULT_FEMALE_AVATAR_PATH : DEFAULT_AVATAR_PATH;
  const defaultSvgDataUri = isFemale ? DEFAULT_FEMALE_AVATAR_SVG_DATA_URI : DEFAULT_AVATAR_SVG_DATA_URI;
  const defaultSvgFilename = isFemale ? 'uzman-merve.svg' : 'uzman-samet.svg';

  const [photoSrc, setPhotoSrc] = useState<string>(getStoredSpecialistPhoto(effectiveSite));
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    setPhotoSrc(getStoredSpecialistPhoto(effectiveSite));
  }, [effectiveSite]);

  useEffect(() => {
    const hasFetched = effectiveSite === 'kadin' ? hasFetchedFemaleFromServer : hasFetchedMaleFromServer;
    if (!hasFetched) {
      if (effectiveSite === 'kadin') hasFetchedFemaleFromServer = true;
      else hasFetchedMaleFromServer = true;
      syncSpecialistPhotoWithServer(effectiveSite);
    }

    const handlePhotoChanged = (e: any) => {
      const targetSite = e?.detail?.site || 'erkek';
      if (targetSite === effectiveSite && e?.detail?.photo) {
        setPhotoSrc(e.detail.photo);
        setHasError(false);
      }
    };

    window.addEventListener('specialist-photo-changed', handlePhotoChanged);
    return () => {
      window.removeEventListener('specialist-photo-changed', handlePhotoChanged);
    };
  }, [effectiveSite]);

  const sizeClasses: Record<SpecialistAvatarSize, { container: string; img: string; status: string }> = {
    xs: { container: 'w-6 h-6 min-w-6', img: 'w-6 h-6', status: 'w-1.5 h-1.5 bottom-0 right-0' },
    sm: { container: 'w-8 h-8 min-w-8', img: 'w-8 h-8', status: 'w-2 h-2 bottom-0 right-0' },
    md: { container: 'w-10 h-10 min-w-10', img: 'w-10 h-10', status: 'w-2.5 h-2.5 bottom-0 right-0' },
    lg: { container: 'w-12 h-12 min-w-12', img: 'w-12 h-12', status: 'w-3 h-3 bottom-0.5 right-0.5' },
    xl: { container: 'w-16 h-16 min-w-16', img: 'w-16 h-16', status: 'w-3.5 h-3.5 bottom-0.5 right-0.5' },
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  const isCustomPhoto =
    photoSrc &&
    photoSrc !== defaultPath &&
    photoSrc !== defaultSvgDataUri &&
    !photoSrc.includes(defaultSvgFilename);

  return (
    <div
      onClick={onClick}
      className={`relative inline-flex items-center justify-center shrink-0 rounded-full select-none ${
        currentSize.container
      } ${
        bordered
          ? isFemale
            ? 'ring-2 ring-rose-400/40 shadow-xs'
            : 'ring-2 ring-amber-400/40 shadow-xs'
          : ''
      } ${onClick ? 'cursor-pointer hover:opacity-90' : ''} ${className}`}
    >
      {isCustomPhoto && !hasError ? (
        <img
          src={photoSrc}
          alt={isFemale ? 'Uzman Kadın Estetisyen' : 'Uzman Estetisyen'}
          className={`rounded-full object-cover object-center w-full h-full shadow-inner`}
          onError={() => setHasError(true)}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full rounded-full overflow-hidden bg-slate-800 flex items-center justify-center">
          <img
            src={defaultPath}
            alt={isFemale ? 'Uzman Kadın Estetisyen' : 'Uzman Estetisyen'}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to embedded SVG data URI if static file fails to load
              const target = e.currentTarget;
              if (target.src !== defaultSvgDataUri) {
                target.src = defaultSvgDataUri;
              }
            }}
          />
        </div>
      )}

      {showOnlineStatus && (
        <span
          className={`absolute ${currentSize.status} rounded-full bg-emerald-500 ring-2 ring-white z-10 flex items-center justify-center`}
          title="Çevrim İçi - Hemen Yanıt Veriyor"
        >
          <span className="w-full h-full rounded-full bg-emerald-400 animate-ping opacity-75" />
        </span>
      )}
    </div>
  );
};

interface SpecialistBadgeProps {
  compact?: boolean;
  className?: string;
  onClick?: () => void;
  site?: SiteTarget;
}

export const SpecialistBadge: React.FC<SpecialistBadgeProps> = ({
  compact = false,
  className = '',
  onClick,
  site,
}) => {
  let siteFromContext: SiteTarget = 'erkek';
  try {
    const context = useSite();
    if (context && context.siteKey) {
      siteFromContext = context.siteKey;
    }
  } catch {}

  const effectiveSite = site || siteFromContext;
  const isFemale = effectiveSite === 'kadin' || effectiveSite === 'apollon';

  return (
    <div
      onClick={onClick}
      className={`inline-flex items-center gap-2.5 bg-slate-900/95 text-white p-2 sm:p-2.5 rounded-2xl border border-slate-700/80 shadow-md ${
        onClick ? (isFemale ? 'cursor-pointer hover:border-rose-400/50' : 'cursor-pointer hover:border-amber-400/50') : ''
      } transition-colors ${className}`}
    >
      <SpecialistAvatar size={compact ? 'sm' : 'md'} site={effectiveSite} />
      <div className="text-left min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs sm:text-sm font-black text-white tracking-tight">
            {effectiveSite === 'apollon' ? 'Apollon Medikal Uzmanı' : isFemale ? 'Uzman Kadın Estetisyen' : 'Uzman Estetisyen'}
          </span>
          <span
            className={`text-[10px] font-bold ${
              effectiveSite === 'apollon'
                ? 'text-amber-300 bg-amber-500/20 border-amber-300/30'
                : isFemale
                ? 'text-rose-400 bg-rose-500/20 border-rose-400/30'
                : 'text-amber-400 bg-amber-500/20 border-amber-400/30'
            } px-1.5 py-0.2 rounded border`}
          >
            Klinik Sorumlusu
          </span>
        </div>
        <span className="text-[10px] sm:text-[11px] text-slate-300 block truncate">
          {effectiveSite === 'apollon' ? 'FSM Bulvarı • Apollon Güzellik Merkezi' : isFemale ? 'FSM Bulvarı • Kadın Lazer Epilasyon' : 'FSM Bulvarı • Erkek Lazer Epilasyon'}
        </span>
      </div>
    </div>
  );
};
